import { CSSObject } from 'styled-components'
import { Theme } from '@/src/ct-design/token/theme'
import { StepperSize } from './type'
import { getConfig } from './config'

export type StepStatus = 'COMPLETED' | 'CURRENT' | 'UPCOMING'

interface StepStyleProps {
    theme: Theme
    status: StepStatus
    isClickable?: boolean
    isLastStep?: boolean
    size?: StepperSize
}

interface StepDividerStyleProps {
    theme: Theme
    status: StepStatus
    size?: StepperSize
}

export const getStepperContainerStyles = ({
    theme
}: {
    theme: Theme
}): CSSObject => {
    return {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%'
    }
}

export const getStepItemContainerStyles = ({
    isClickable,
    isLastStep
}: StepStyleProps): CSSObject => {
    return {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
        flex: isLastStep ? 'none' : 1,
        cursor: isClickable ? 'pointer' : 'default'
    }
}

export const getStepContentContainerStyles = ({
    theme,
    size = 'md'
}: {
    theme: Theme
    size?: StepperSize
}): CSSObject => {
    const config = getConfig(size)

    return {
        display: 'flex',
        flexDirection: config.layout.contentDirection as 'column' | 'row',
        alignItems: 'center',
        gap:
            typeof config.layout.gap === 'function'
                ? config.layout.gap(theme)
                : config.layout.gap,
        overflowY: 'hidden'
    }
}

export const getStepCircleStyles = ({
    theme,
    status,
    size = 'md'
}: StepStyleProps): CSSObject => {
    const isCompleted = status === 'COMPLETED'
    const isActive = status === 'CURRENT'
    const config = getConfig(size)

    return {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: config.circle.height,
        width: config.circle.width,
        borderRadius: '100%',
        backgroundColor: isCompleted
            ? theme.color.stepper.bg.completedBg
            : theme.color.stepper.bg.upcomingBg,
        border: !isCompleted
            ? `2px solid ${isActive ? theme.color.stepper.border.currentBorder : theme.color.stepper.border.upcomingBorder}`
            : 'none'
    }
}

export const getStepTitleStyles = ({
    theme,
    status,
    size = 'md'
}: StepStyleProps): CSSObject => {
    const isCompleted = status === 'COMPLETED'
    const isActive = status === 'CURRENT'
    const config = getConfig(size)

    let color
    if (isCompleted) {
        color = theme.color.text.link
    } else if (isActive) {
        color = theme.color.text.primary
    } else {
        color = theme.color.text.subHeading
    }

    return {
        marginLeft:
            typeof config.title.marginLeft === 'function'
                ? config.title.marginLeft(theme)
                : config.title.marginLeft,
        color,
        fontSize: config.title.fontSize,
        textAlign: config.title.textAlign as 'center' | 'left',
        maxWidth: config.title.maxWidth,
        wordWrap: 'break-word'
    }
}

export const getStepDividerStyles = ({
    theme,
    status,
    size = 'md'
}: StepDividerStyleProps): CSSObject => {
    const isCompleted = status === 'COMPLETED'
    const isActive = status === 'CURRENT'
    const config = getConfig(size)
    const margin =
        typeof config.divider.margin === 'function'
            ? config.divider.margin(theme)
            : config.divider.margin

    return {
        flex: 1,
        marginLeft: margin,
        marginRight: margin,
        backgroundColor: isCompleted
            ? theme.color.stepper.divider.completedBg
            : isActive
              ? theme.color.stepper.divider.currentBg
              : theme.color.stepper.divider.upcomingBg,
        height: '2px'
    }
}
